import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatMoney, formatDate, daysUntil } from "@/lib/format";
import { useCurrency } from "@/context/CurrencyContext";
import TransactionDialog from "@/components/TransactionDialog";
import { Button } from "@/components/ui/button";
import { CalendarClock, Plus, Check, Trash2, ArrowDownRight, ArrowUpRight } from "lucide-react";

export default function Upcoming() {
  const { base } = useCurrency();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState({ receivables: 0, payables: 0 });

  const load = useCallback(async () => {
    const [a, c, s] = await Promise.all([
      api.get("/transactions?status=pending"),
      api.get("/categories"),
      api.get(`/summary?base=${base}`),
    ]);
    setItems(a.data.sort((x, y) => new Date(x.date) - new Date(y.date)));
    setCats(c.data);
    setSummary({ receivables: s.data.upcoming_receivables, payables: s.data.upcoming_payables });
  }, [base]);

  useEffect(() => { load(); }, [load]);

  const receivables = useMemo(() => items.filter((i) => i.type === "income"), [items]);
  const payables = useMemo(() => items.filter((i) => i.type === "expense"), [items]);

  const settle = async (id) => {
    try {
      await api.post(`/transactions/${id}/settle`);
      toast.success("Gerçekleşen olarak işaretlendi");
      load();
    } catch { toast.error("İşlem başarısız"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    try { await api.delete(`/transactions/${id}`); toast.success("Silindi"); load(); } catch { toast.error("Silinemedi"); }
  };

  const catName = (id) => cats.find((c) => c.id === id)?.name || "—";
  const catColor = (id) => cats.find((c) => c.id === id)?.color || "#64748B";

  const List = ({ title, data, kind }) => (
    <div className="bg-[#131924] border border-white/5 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
            {kind === "income" ? "Alacaklar" : "Borçlar"}
          </div>
          <h2 className="font-display text-xl font-bold tracking-tight mt-1">{title}</h2>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${kind === "income" ? "bg-emerald-500/10" : "bg-rose-500/10"}`}>
          {kind === "income" ? <ArrowUpRight className="w-4 h-4 text-emerald-400" /> : <ArrowDownRight className="w-4 h-4 text-rose-400" />}
        </div>
      </div>

      {data.length === 0 ? (
        <div className="text-sm text-slate-500 py-10 text-center">
          {kind === "income" ? "Yaklaşan tahsilat yok." : "Yaklaşan ödeme yok."}
        </div>
      ) : (
        <div className="space-y-2.5">
          {data.map((u) => {
            const d = daysUntil(u.date);
            const overdue = d < 0;
            const dueToday = d === 0;
            return (
              <div
                key={u.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5"
                data-testid={`${kind}-item-${u.id}`}
              >
                <div
                  className={`w-1 self-stretch rounded-full ${kind === "income" ? "bg-emerald-500" : "bg-rose-500"}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{u.counterparty || u.description || "İsimsiz"}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                      style={{ background: catColor(u.category_id) + "22", color: catColor(u.category_id) }}
                    >
                      {catName(u.category_id)}
                    </span>
                    <span className="text-[11px] text-slate-500 font-mono-num">{formatDate(u.date)}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                      overdue ? "bg-rose-500/15 text-rose-300" :
                      dueToday ? "bg-amber-500/15 text-amber-300" :
                      "bg-indigo-500/15 text-indigo-300"
                    }`}>
                      {overdue ? `${Math.abs(d)}g gecikti` : dueToday ? "Bugün" : `${d}g kaldı`}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`font-mono-num font-semibold ${kind === "income" ? "text-emerald-400" : "text-rose-400"}`}>
                    {kind === "income" ? "+" : "-"}{formatMoney(u.amount, u.currency)}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => settle(u.id)}
                    data-testid={`settle-${u.id}`}
                    className="h-8 w-8 text-emerald-400 hover:bg-emerald-500/10"
                    title="Gerçekleşen olarak işaretle"
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => del(u.id)}
                    data-testid={`delete-upcoming-${u.id}`}
                    className="h-8 w-8 text-slate-400 hover:text-rose-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400">Takvim</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-1">
            Yaklaşan Tahsilat & Ödemeler
          </h1>
          <p className="text-sm text-slate-400 mt-1">Gelen alacaklar ve ödenecek borçları takip edin.</p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          data-testid="upcoming-add-button"
          className="bg-indigo-500 hover:bg-indigo-600"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Yaklaşan Kalem Ekle
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-xl p-5" data-testid="receivables-summary">
          <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-400">Toplam Alacak</div>
          <div className="font-mono-num text-3xl font-bold text-emerald-300 mt-2">{formatMoney(summary.receivables, base)}</div>
          <div className="text-[11px] text-slate-500 mt-1">{receivables.length} adet yaklaşan tahsilat</div>
        </div>
        <div className="bg-rose-500/5 border border-rose-500/10 rounded-xl p-5" data-testid="payables-summary">
          <div className="text-[10px] uppercase tracking-[0.25em] text-rose-400">Toplam Borç</div>
          <div className="font-mono-num text-3xl font-bold text-rose-300 mt-2">{formatMoney(summary.payables, base)}</div>
          <div className="text-[11px] text-slate-500 mt-1">{payables.length} adet yaklaşan ödeme</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <List title="Yaklaşan Tahsilatlar" data={receivables} kind="income" />
        <List title="Yaklaşan Ödemeler" data={payables} kind="expense" />
      </div>

      <TransactionDialog open={open} onOpenChange={setOpen} onSaved={load} defaultStatus="pending" />
    </div>
  );
}
